import { contentfulProps } from 'assets/mockData/emptyTypes';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import Section from 'molecules/Section/Section';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

// Types
import type { FC } from 'react';

const Footer: FC<FooterProps> = ({
  logo,
  logoDisclaimer,
  socialLinks,
  elementMenu,
  copyright,
  legalLinks,
  removeLogoLink,
  ...props
}) => {
  const hasLegalCopyright = copyright || legalLinks;

  return (
    <Section {...contentfulProps} backgroundColor="Navy 900" idLink="fieldroutes-footer">
      <Container as="footer" {...props}>
        <Flex
          paddingBottom={24}
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-start"
          gap="16px"
          borderColor="navy-700"
          md={{
            flexDirection: 'row',
            paddingBottom: elementMenu || hasLegalCopyright ? 48 : 0,
            gap: '32px',
            alignItems: 'flex-end',
          }}
        >
          {logo && (
            <Flex
              width="100%"
              maxWidth="100%"
              flexDirection="column"
              gap="18px"
              md={{ maxWidth: 'fit-content' }}
              css={`
                .MuiButton-root {
                  width: fit-content !important;
                }
              `}
            >
              <Link href={removeLogoLink ? null : '/'} width="fit-content" height="43px">
                <Image image={logo} alt="Footer Logo" width="100%" maxWidth="198px" />
              </Link>
              {logoDisclaimer && (
                <Text textStyle="textXs" textColor="navy-50">
                  {logoDisclaimer}
                </Text>
              )}
              <ComponentButton link="tel:888.779.3101" label="888.779.3101" buttonColor="White Link" />
            </Flex>
          )}
          {socialLinks && (
            <Flex flexWrap="wrap" gap="18px" height="fit-content">
              {socialLinks.map(social => (
                <Link key={social?.internalName} href={social?.url || ''} textDecoration="none" height="24px">
                  {social?.icon && (
                    <Image
                      image={social?.icon}
                      alt={social?.internalName || 'Social Icon Link'}
                      width="24px"
                      height="24px"
                    />
                  )}
                </Link>
              ))}
            </Flex>
          )}
        </Flex>
        {elementMenu && (
          <Flex
            flexDirection="column"
            justifyContent="space-between"
            gap="36px"
            width="100%"
            paddingY={40}
            borderBottom="1px solid"
            borderTop="1px solid"
            borderColor="navy-700"
            lg={{ flexDirection: 'row' }}
          >
            {elementMenu && (
              <Flex
                flexWrap="wrap"
                width="100%"
                justifyContent="flex-start"
                gap="32px"
                lg={{ justifyContent: 'space-between' }}
              >
                {elementMenu.map(parent => (
                  <Flex
                    key={parent?.heading}
                    flexDirection="column"
                    gap="16px"
                    width="100%"
                    maxWidth="calc(50% - 16px)"
                    sm={{ maxWidth: 'calc((100% - 96px) / 4)' }}
                    lg={{ maxWidth: 'fit-content' }}
                  >
                    <Text textStyle="textSm" fontWeight="bold" textColor="gray-50">
                      {parent?.heading}
                    </Text>
                    <Flex flexDirection="column" gap="8px">
                      {parent?.menuItem &&
                        parent.menuItem.map(submenu => (
                          <Link
                            key={submenu?.heading}
                            href={`${submenu?.url}`}
                            textColor="navy-50"
                            fontSize="textXs"
                            textDecoration="none"
                          >
                            {submenu?.heading}
                          </Link>
                        ))}
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        )}
        {hasLegalCopyright && (
          <Flex
            paddingTop={24}
            flexDirection="column"
            justifyContent="space-between"
            alignItems="flex-start"
            gap="16px"
            md={{ flexDirection: 'row', paddingTop: 48, gap: '32px' }}
            lg={{ alignItems: 'flex-end' }}
          >
            {copyright && (
              <Flex
                width="100%"
                maxWidth="100%"
                flexDirection="column"
                gap="10px"
                textColor="navy-50"
                md={{ maxWidth: 'fit-content' }}
                css={`
                  h6 {
                    font-size: 16px;
                    line-height: 24px;
                  }
                  p {
                    font-size: 12px;
                    line-height: 18px;
                  }
                `}
              >
                {richTextParser(copyright)}
              </Flex>
            )}
            {legalLinks && (
              <Flex flexWrap="wrap" gap="16px 24px" height="fit-content">
                {legalLinks.map(legal => (
                  <Link key={legal?.heading} href={legal?.url || ''} textColor="navy-50" className="text-[13px]">
                    {legal?.heading}
                  </Link>
                ))}
                {/** See DG-5892, OneTrust button is configured via GTM */}
                <div
                  className="ot-cookie-setting-btn hover:cursor-pointer"
                  onClick={() => window?.OneTrust?.ToggleInfoDisplay()}
                  role="presentation"
                >
                  <p className="text-[13px] text-navy-50 underline">Do Not Sell or Share My Personal Information</p>
                </div>
              </Flex>
            )}
          </Flex>
        )}
      </Container>
    </Section>
  );
};

export default Footer;
